import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from './api'

export type Severity = 'info' | 'warning' | 'error' | 'critical'

export interface Alert {
  id: string
  fingerprint: string
  rule_name: string
  severity: Severity
  title: string
  details: Record<string, unknown>
  entity_kind: string | null
  entity_id: string | null
  entity_name: string | null
  opened_at: string
  last_seen_at: string
  closed_at: string | null
  acknowledged_at?: string | null
  acknowledged_by?: string | null
}

export interface AlertRule {
  id: string
  name: string
  severity: Severity
  description: string
  enabled: boolean
  channels: string[]
  params: Record<string, unknown>
}

export interface Silence {
  id: string
  fingerprint: string
  reason: string | null
  expires_at: string
}

export interface Channel {
  id: string
  name: string
  kind: 'resend' | 'telegram'
  enabled: boolean
}

export function useAlerts(openOnly: boolean = false) {
  return useQuery<Alert[]>({
    queryKey: ['alerts', { openOnly }],
    queryFn: () =>
      api<Alert[]>(`/api/alerts${openOnly ? '?open_only=true' : ''}`),
    refetchInterval: 10_000,
  })
}

export function useAlert(id: string | undefined) {
  return useQuery<Alert>({
    queryKey: ['alert', id],
    queryFn: () => api<Alert>(`/api/alerts/${id!}`),
    enabled: !!id,
  })
}

export function useAlertRules() {
  return useQuery<AlertRule[]>({
    queryKey: ['alert-rules'],
    queryFn: () => api<AlertRule[]>('/api/alert-rules'),
  })
}

export function useSilences() {
  return useQuery<Silence[]>({
    queryKey: ['silences'],
    queryFn: () => api<Silence[]>('/api/silences'),
  })
}

export function useCreateSilence() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { fingerprint: string; duration_seconds: number; reason?: string }) =>
      api<Silence>('/api/silences', {
        method: 'POST',
        body: JSON.stringify(vars),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['silences'] })
      qc.invalidateQueries({ queryKey: ['alerts'] })
    },
  })
}

export function useDeleteSilence() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      api(`/api/silences/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['silences'] }),
  })
}

export function useChannels() {
  return useQuery<Channel[]>({
    queryKey: ['channels'],
    queryFn: () => api<Channel[]>('/api/channels'),
  })
}

export function useTestChannel() {
  return useMutation({
    mutationFn: (name: string) =>
      api('/api/channels/test', {
        method: 'POST',
        body: JSON.stringify({ channel_name: name }),
      }),
  })
}

export interface MaintenanceWindow {
  id: string
  name: string
  starts_at: string
  ends_at: string
  scope: Record<string, unknown>
}

export function useAckAlert() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api(`/api/alerts/${id}/ack`, { method: 'POST' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['alerts'] })
      // The detail view is keyed ['alert', id] — a different first segment, so
      // prefix matching on ['alerts'] never reaches it and the page would keep
      // showing the un-acknowledged state until a manual reload.
      qc.invalidateQueries({ queryKey: ['alert'] })
    },
  })
}

export function useMaintenanceWindows() {
  return useQuery<MaintenanceWindow[]>({
    queryKey: ['maintenance-windows'],
    queryFn: () => api<MaintenanceWindow[]>('/api/maintenance-windows'),
  })
}

export function useCreateMw() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { name: string; starts_at: string; ends_at: string; scope?: Record<string, unknown> }) =>
      api('/api/maintenance-windows', { method: 'POST', body: JSON.stringify(vars) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['maintenance-windows'] }),
  })
}

export function useDeleteMw() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api(`/api/maintenance-windows/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['maintenance-windows'] }),
  })
}

export interface UpdateRuleBody {
  enabled?: boolean
  channels?: string[]
  params?: Record<string, unknown>
}

export function useUpdateRule() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateRuleBody }) =>
      api(`/api/alert-rules/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['alert-rules'] }),
  })
}
